import { useState } from 'react';
import { ValidateTokenSuccessResponse, formatExpiresIn } from '../../types/signatureToken';
import { useSubmitSignatureMutation } from '../../services/hooks/useSignatureToken';
import SignaturePad from './SignaturePad';
import SignatureSuccessView from './SignatureSuccessView';
import SignatureErrorView from './SignatureErrorView';
import { toast } from 'react-toastify';

// ========================================
// PROPS INTERFACE
// ========================================

interface SignatureFormViewProps {
    data: ValidateTokenSuccessResponse['data'];
}

// ========================================
// SIGNATURE FORM VIEW COMPONENT
// ========================================

/**
 * Vista principal con formulario de firma
 * Incluye:
 * - Header con tiempo restante
 * - Información del cliente
 * - Botón para ver PDF del contrato
 * - Formulario de DNI + Firma digital
 * - Estados de éxito/error
 */
const SignatureFormView = ({ data }: SignatureFormViewProps) => {
    const { contract, client, token } = data;
    const timeRemaining = formatExpiresIn(token.expiresIn);
    const tokenParam = window.location.pathname.split('/').pop();

    // Lógica defensiva: Si no existe firmante en el backend, construir desde cliente
    const firmante = data.firmante || {
        nombre: client.nombre, // Nombre completo del cliente
        dni: client.dni,
        tipo: 'cliente' as const
    };

    // Detectar si el cliente firma su propio contrato o lo firma otra persona
    const isClientSigning = firmante.tipo === 'cliente';
    const showSeparateInfo = !isClientSigning; // Mostrar sección separada para firmante

    // Estados del formulario
    const [dniInput, setDniInput] = useState('');
    const [dniVerified, setDniVerified] = useState(false); // Control de DNI verificado
    const [signatureData, setSignatureData] = useState('');
    const [formState, setFormState] = useState<'form' | 'success' | 'error'>('form');
    const [errorMessage, setErrorMessage] = useState('');
    const [submitResult, setSubmitResult] = useState<any>(null); // Guardar resultado de la firma
    
    // Mutation para enviar firma
    const submitMutation = useSubmitSignatureMutation();

    /**
     * Verificar DNI antes de permitir ver el contrato y firmar
     */
    const handleVerifyDni = () => {
        const normalizedInput = dniInput.toUpperCase().trim();
        const normalizedRequired = data.requiredDni.toUpperCase().trim();
        
        if (!dniInput) {
            toast.error('Debe ingresar su DNI para continuar');
            return;
        }
        
        if (normalizedInput !== normalizedRequired) {
            toast.error('El DNI ingresado no coincide con el autorizado para firmar este documento');
            setDniInput('');
            return;
        }
        
        setDniVerified(true);
        toast.success('✅ Identidad verificada correctamente');
    };

    /**
     * Validar y enviar firma (DNI ya verificado previamente)
     */
    const handleSubmit = async () => {
        // Validar firma
        if (!signatureData) {
            toast.error('Debe firmar el documento antes de continuar');
            return;
        }
        
        try {
            const result = await submitMutation.mutateAsync({
                token: tokenParam!,
                clientDni: dniInput,
                signatureDataURL: signatureData
            });
            
            if (result.success) {
                setSubmitResult(result.data); // Guardar datos de la respuesta
                setFormState('success');
            } else {
                setFormState('error');
                setErrorMessage(result.message);
            }
        } catch (error: any) {
            setFormState('error');
            setErrorMessage(error.response?.data?.message || 'Error al procesar la firma');
        }
    };

    // Mostrar vista de éxito
    if (formState === 'success') {
        return <SignatureSuccessView result={submitResult} />;
    }

    // Mostrar vista de error
    if (formState === 'error') {
        return <SignatureErrorView 
            message={errorMessage}
            onRetry={() => {
                setFormState('form');
                setErrorMessage('');
            }}
        />;
    }

    // Formulario normal
    return (
        <div className="flex flex-col w-full h-screen overflow-hidden bg-gray-50">
            <div className="flex-1 overflow-y-auto py-4 sm:py-8 px-3 sm:px-4">
                <div className="max-w-3xl mx-auto pb-6">
                    {/* Header */}
                    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
                                Firma de Contrato
                            </h1>
                            <div className="flex items-center text-xs sm:text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="whitespace-nowrap">Expira en: <strong>{timeRemaining}</strong></span>
                            </div>
                        </div>
                        
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded">
                            <p className="text-xs sm:text-sm text-blue-800">
                                <strong>Firmante:</strong> {firmante.nombre}
                            </p>
                            {showSeparateInfo && (
                                <p className="text-xs sm:text-sm text-blue-600 mt-1">
                                    Firmando en representación del cliente
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Información del Cliente */}
                    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
                            {showSeparateInfo ? 'Información del Cliente' : 'Información Personal'}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Nombre Completo
                                </label>
                                <p className="text-sm sm:text-base text-gray-900 break-words">{client.nombre}</p>
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <p className="text-sm sm:text-base text-gray-900 break-all">{client.email}</p>
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Estado del Contrato
                                </label>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    contract.estado === 'pendiente' 
                                    ? 'bg-yellow-100 text-yellow-800' 
                                    : contract.estado === 'firmado'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {contract.estado.toUpperCase()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Información del Firmante (cuando no es el cliente) */}
                    {showSeparateInfo && (
                        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
                                Datos del Pagador/Responsable que Firma
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div>
                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                        Nombre Completo
                                    </label>
                                    <p className="text-sm sm:text-base text-gray-900 break-words">{firmante.nombre}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Verificación de Identidad */}
                    {!dniVerified ? (
                        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-blue-500">
                            <div className="flex items-start mb-4">
                                <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                <div className="flex-1">
                                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                                        Verificación de Identidad
                                    </h2>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Por seguridad, debe verificar su identidad antes de ver el contrato y firmar.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {isClientSigning ? 'Ingrese su DNI *' : 'DNI del Firmante *'}
                                    </label>
                                    <input
                                        type="text"
                                        value={dniInput}
                                        onChange={(e) => setDniInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleVerifyDni()}
                                        placeholder={isClientSigning ? 'Tu DNI (Ej: 12345678A)' : 'DNI del firmante (Ej: 12345678A)'}
                                        className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                        maxLength={20}
                                        autoFocus
                                    />
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={handleVerifyDni}
                                    disabled={!dniInput}
                                    className={`w-full px-6 py-3 rounded-lg transition text-base font-medium ${
                                        !dniInput
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                                    }`}
                                >
                                    🔐 Verificar Identidad
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Botón para ver contrato PDF */}
                            {contract.pdfUrl ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 sm:mb-6">
                                    <div className="flex items-center text-green-800 mb-3">
                                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-medium">Identidad verificada correctamente</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                        <div className="flex items-start">
                                            <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <div>
                                                <p className="font-medium text-gray-900 text-sm sm:text-base">Documento a Firmar</p>
                                                <p className="text-xs sm:text-sm text-gray-700 mt-1">
                                                    Revise el contrato antes de firmar
                                                </p>
                                            </div>
                                        </div>
                                        <a
                                            href={contract.pdfUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm sm:text-base transition"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            Ver Contrato PDF
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 sm:mb-6">
                                    <div className="flex items-center text-gray-600">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <p className="text-sm">PDF del contrato no disponible temporalmente</p>
                                    </div>
                                </div>
                            )}

                            {/* Formulario de Firma */}
                            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
                                    Formulario de Firma
                                </h2>

                                <div className="space-y-4">
                                    {/* Firma Digital con SignaturePad */}
                                    <div>
                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                            Firma Digital *
                                        </label>
                                        <SignaturePad
                                            onSignatureChange={(dataURL) => setSignatureData(dataURL)}
                                            onClear={() => setSignatureData('')}
                                            disabled={false}
                                        />
                                        <p className="text-xs text-gray-500 mt-2">
                                            Firme con su dedo o mouse en el recuadro de arriba
                                        </p>
                                    </div>

                                    {/* Botones */}
                                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
                                        <button
                                            type="button"
                                            className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-gray-300 text-gray-700 text-sm sm:text-base rounded-lg hover:bg-gray-50 transition order-2 sm:order-1"
                                            onClick={() => window.close()}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={!signatureData || submitMutation.isPending}
                                            className={`w-full sm:w-auto px-4 sm:px-6 py-2 text-sm sm:text-base rounded-lg transition order-1 sm:order-2 ${
                                                !signatureData || submitMutation.isPending
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                                            }`}
                                        >
                                            {submitMutation.isPending ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Procesando...
                                                </span>
                                            ) : (
                                                '✍️ Firmar Documento'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Footer */}
                    <div className="text-center mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500">
                        <p className="break-all px-4">ID del Contrato: {contract.id}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignatureFormView;
